/**
 * Formats a date string to a readable format (e.g., "Jan 2023")
 * @param dateString - ISO date string or null
 * @param config - Optional configuration object
 * @param config.locale - Locale for date formatting (default: "en-US")
 * @returns Formatted date string or null when value is missing/invalid
 */
export function formatDate(
  dateString: string | null,
  config?: { locale?: "en-US" | "es-AR" | "es-ES" }
): string | null {
  if (!dateString) {
    return null;
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const locale = config?.locale || "en-US";

  return date.toLocaleDateString(locale, { year: "numeric", month: "short" });
}

/**
 * Calculates the duration between two dates in human-readable format
 * @param startDate - ISO date string
 * @param endDate - ISO date string or null (null means ongoing)
 * @returns Duration string (e.g., "2 years 3 months")
 */
export function calculateDuration(
  startDate: string,
  endDate: string | null
): string {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();

  const months =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth());
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  const parts: string[] = [];
  if (years > 0) {
    parts.push(`${years} year${years > 1 ? "s" : ""}`);
  }
  if (remainingMonths > 0) {
    parts.push(`${remainingMonths} month${remainingMonths > 1 ? "s" : ""}`);
  }

  return parts.join(" ") || "Less than a month";
}

export function getFirstYearFromDates(startDates: string[]): number | null {
  if (!startDates.length) {
    return null;
  }

  const years = startDates
    .map((startDate) => new Date(startDate).getFullYear())
    .filter((year) => !Number.isNaN(year));

  if (!years.length) {
    return null;
  }

  return Math.min(...years);
}

export function calculateYearsOfExperience(
  startDates: string[]
): number | null {
  const firstYear = getFirstYearFromDates(startDates);
  if (!firstYear) {
    return null;
  }

  const currentYear = new Date().getFullYear();
  return Math.max(1, currentYear - firstYear);
}
